import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../services/api';

interface VerifyResult {
  valid: boolean;
  reason?: string;
  serial?: string;
  recipientName?: string;
  courseTitle?: string;
  issuedBy?: string;
  issuedAt?: string;
  revokedAt?: string;
}

/**
 * Public certificate verification.
 *
 * No token, no session, no account. A hiring school types the serial off a
 * printout, and this says yes or no.
 *
 * What it deliberately does *not* return is anything beyond what is already
 * printed on the certificate itself — no email address, no membership id, no
 * organisation id. The serial is twelve characters from a 31-character alphabet
 * chosen to avoid the ones people mistype, so it cannot be enumerated either.
 *
 * The page is prerendered without a serial and fetches on demand, so it is
 * indexable and works when a certificate is checked from a QR code.
 */
export function VerifyPage() {
  const { serial: fromUrl } = useParams();
  const navigate = useNavigate();
  const [serial, setSerial] = useState(fromUrl ?? '');
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (fromUrl) void check(fromUrl);
    // Only on the first render with a serial in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  async function check(value: string) {
    const clean = value.trim().toUpperCase();
    if (!clean) return;
    setBusy(true);
    try {
      setResult(await apiClient.open<VerifyResult>(`/verify/${encodeURIComponent(clean)}`));
    } catch (error) {
      setResult({ valid: false, reason: (error as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-14">
      <h1 className="text-3xl font-bold text-ink-900">Verify a certificate</h1>
      <p className="mt-2 text-ink-700">
        Enter the serial printed on the certificate. No account is needed.
      </p>

      <form
        className="mt-6 flex flex-wrap gap-2"
        onSubmit={event => {
          event.preventDefault();
          navigate(`/verify/${serial.trim().toUpperCase()}`);
          void check(serial);
        }}
      >
        <input
          className="input flex-1 font-mono uppercase tracking-widest"
          value={serial}
          onChange={event => setSerial(event.target.value)}
          placeholder="XXXX-XXXX-XXXX"
          aria-label="Certificate serial"
        />
        <button type="submit" className="btn-primary" disabled={busy || !serial.trim()}>
          {busy ? 'Checking…' : 'Verify'}
        </button>
      </form>

      {result && (
        <div
          className={`card mt-6 ${
            result.valid ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50'
          }`}
        >
          {result.valid ? (
            <>
              <p className="text-sm font-semibold text-emerald-800">✓ This certificate is genuine</p>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-ink-500">Awarded to</dt>
                  <dd className="font-medium text-ink-900">{result.recipientName}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-ink-500">For</dt>
                  <dd className="font-medium text-ink-900">{result.courseTitle}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-ink-500">Issued by</dt>
                  <dd className="text-ink-800">{result.issuedBy}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-ink-500">Issued on</dt>
                  <dd className="text-ink-800">
                    {result.issuedAt ? new Date(result.issuedAt).toLocaleDateString() : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-ink-500">Serial</dt>
                  <dd className="font-mono text-ink-800">{result.serial}</dd>
                </div>
              </dl>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-red-800">✗ Not verified</p>
              <p className="mt-1 text-sm text-red-700">{result.reason}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
